import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, TriangleAlert, BellRing } from "lucide-react";
import { IncidentDialog } from "@/components/portal/IncidentDialog";

export default function Incidents() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = async () => {
    const [i, c] = await Promise.all([api.get("/portal/incidents"), api.get("/portal/clients")]);
    setItems(i.data);
    setClients(c.data);
    setLoading(false);
  };
  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    if (clients.length === 0) return toast.error("Add a client first.");
    setOpen(true);
  };

  const remove = async (id) => {
    await api.delete(`/portal/incidents/${id}`);
    toast.success("Incident deleted");
    load();
  };

  const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";

  return (
    <div className="space-y-8" data-testid="incidents-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-4xl text-charcoal">Emergency & Incidents</h1>
          <p className="text-charcoal-soft mt-1">Document anything unexpected — and alert family instantly.</p>
        </div>
        <button onClick={openNew} data-testid="add-incident-button" className="inline-flex items-center gap-2 rounded-full bg-terracotta text-white px-5 py-3 text-sm font-medium hover:opacity-90 transition-opacity">
          <Plus className="h-4 w-4" /> Log Incident
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-sage" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-charcoal/5 soft-shadow p-14 text-center">
          <TriangleAlert className="h-10 w-10 text-sage/50 mx-auto mb-4" />
          <p className="text-charcoal-soft">No incidents logged. Hopefully it stays that way. 🌿</p>
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((it) => (
            <div key={it.id} data-testid={`incident-row-${it.id}`} className="bg-white rounded-2xl border-l-4 border-terracotta border border-charcoal/5 soft-shadow p-6 group">
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-serif text-xl text-charcoal">{clientName(it.client_id)}</p>
                    {it.alert_sent && <span className="text-xs px-2.5 py-0.5 rounded-full bg-terracotta/15 text-terracotta flex items-center gap-1"><BellRing className="h-3 w-3" /> Family alerted</span>}
                    {it.resolved && <span className="text-xs px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Resolved</span>}
                  </div>
                  <p className="text-sm text-charcoal-soft">{it.occurred_at?.replace("T", " ")}</p>
                </div>
                <button onClick={() => remove(it.id)} className="p-2 text-charcoal/40 hover:text-destructive sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"><Trash2 className="h-4 w-4" /></button>
              </div>
              <div className="mt-3 space-y-1 text-sm">
                <p><span className="text-charcoal-soft">What happened:</span> {it.what_happened}</p>
                {it.action_taken && <p><span className="text-charcoal-soft">Action taken:</span> {it.action_taken}</p>}
                {it.notified && <p><span className="text-charcoal-soft">Notified:</span> {it.notified}</p>}
                {it.outcome && <p><span className="text-charcoal-soft">Outcome:</span> {it.outcome}</p>}
              </div>
            </div>
          ))}
        </div>
      )}

      <IncidentDialog open={open} onOpenChange={setOpen} clients={clients} onSaved={load} />
    </div>
  );
}
